import { ExportConfigStep2 } from 'src/components/common/export-component/types'
import { DefaultErrorState } from 'src/lib/types/discounts'
import { classNames, isValidDate, isValidEndDate } from 'src/lib/utils/helpers'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState } from 'src/assets/DiscountsConfig'
import { useCallback, useEffect, useRef, useState } from 'react'
import { addDays, differenceInMonths, subMonths } from 'date-fns'
import { Info } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import DatePicker from 'src/components/common/calendar/DatePicker'

export type NewExportFormErrorState = {
  emails: DefaultErrorState[]
  email?: DefaultErrorState
  startDate: DefaultErrorState
  endDate: DefaultErrorState
  reportName: DefaultErrorState
}

const initialFormErrorState = {
  startDate: { status: false, errorCode: '' },
  endDate: { status: false, errorCode: '' }
}

export default function ExportReportOnce({
  onValidationChange,
  config,
  onChange,
  variant = 'default'
}: {
  onValidationChange: (isValid: boolean) => void
  config: ExportConfigStep2
  onChange: (config: ExportConfigStep2) => void
  variant?: 'default' | 'reports'
}) {
  const isFirstRender = useRef(true)

  const [startDate, setStartDate] = useState<Date>(
    config.dateRange.startDate ? new Date(config.dateRange.startDate) : addDays(new Date(), -2)
  )
  const [endDate, setEndDate] = useState<Date>(
    config.dateRange.endDate ? new Date(config.dateRange.endDate) : addDays(new Date(), -1)
  )
  const [formErrors, setFormErrors] = useState(initialFormErrorState)

  const minDate = addDays(new Date(), -90)
  const maxDate = addDays(new Date(), -1)

  const isValidForm = useCallback(() => {
    if (!isValidDate(startDate) || startDate.setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0)) {
      setFormErrors(old => ({ ...old, startDate: { status: true, errorCode: 'DC_FORM_017' } }))
      return
    }

    if (!isValidDate(endDate) || endDate.setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0)) {
      setFormErrors(old => ({ ...old, endDate: { status: true, errorCode: 'DC_FORM_018' } }))
      return
    }

    const requireStrictRange = variant === 'reports'
    if (!isValidEndDate(endDate, startDate, !requireStrictRange)) {
      setFormErrors(old => ({
        ...old,
        endDate: { status: true, errorCode: requireStrictRange ? 'DC_FORM_043' : 'DC_FORM_040' }
      }))
      return
    } else if (differenceInMonths(endDate, startDate) >= 3) {
      setFormErrors(old => ({ ...old, endDate: { status: true, errorCode: 'DC_FORM_039' } }))
      return
    }

    setFormErrors(initialFormErrorState)
  }, [startDate, endDate, minDate, variant])

  useEffect(() => {
    onValidationChange(!(formErrors.startDate.status || formErrors.endDate.status))
  }, [formErrors, onValidationChange])

  useEffect(() => {
    if (!isFirstRender.current) isValidForm()
    else isFirstRender.current = false
  }, [startDate, endDate, isValidForm])

  const isReports = variant === 'reports'
  const fieldClass = isReports ? 'flex flex-1 flex-col [&_button]:!h-9 [&_button]:!w-full' : 'flex flex-col'

  // Reports variant: a single combined amber warning row (start error takes priority over end).
  const activeErrorCode = formErrors.startDate.status
    ? formErrors.startDate.errorCode
    : formErrors.endDate.status
      ? formErrors.endDate.errorCode
      : ''
  const errorMessage = activeErrorCode ? errorMappings[activeErrorCode as keyof typeof errorMappings] : ''

  return (
    <div className={classNames('flex flex-col gap-1 w-full', isReports ? '' : 'max-w-4xl')}>
      <div className='flex items-start gap-3'>
        {/* Start Date */}
        <div className={fieldClass}>
          <label className='text-text-xs-medium text-zinc-500 mb-1'>From</label>
          <DatePicker
            date={startDate}
            onChange={date => {
              if (date) {
                setStartDate(date)
                setFormErrors(old => ({ ...old, startDate: defaultErrorState }))
                onChange({
                  ...config,
                  dateRange: { ...config.dateRange, startDate: date.toISOString() }
                })
              }
            }}
            min={minDate}
            max={maxDate}
            disableFutureDates
          />
          {!isReports && formErrors.startDate.status && (
            <TextFieldError message={errorMappings[formErrors.startDate.errorCode as keyof typeof errorMappings]} />
          )}
        </div>

        {/* End Date */}
        <div className={fieldClass}>
          <label className='text-text-xs-medium text-zinc-500 mb-1'>To</label>
          <DatePicker
            date={endDate}
            onChange={date => {
              if (date) {
                setEndDate(date)
                setFormErrors(old => ({ ...old, endDate: defaultErrorState }))
                onChange({
                  ...config,
                  dateRange: { ...config.dateRange, endDate: date.toISOString() }
                })
              }
            }}
            min={minDate}
            max={maxDate}
            disableFutureDates={true}
          />
          {!isReports && formErrors.endDate.status && (
            <TextFieldError message={errorMappings[formErrors.endDate.errorCode as keyof typeof errorMappings]} />
          )}
        </div>
      </div>

      {isReports && errorMessage && (
        <div className='flex items-center gap-1 pt-3'>
          <Icon IconComponent={Info} size='xxs' className='shrink-0 text-amber-700' />
          <span className='text-text-xs-regular text-amber-700'>{errorMessage}</span>
        </div>
      )}
    </div>
  )
}
